/**
 * What the review of one table knows before anything is written: every entity with its
 * identifier, the row text that becomes searchable, what blocks adding, and whether only row
 * text would be written. Built once per table; the outline, the panel above the button and the
 * button read the same facts. Pure.
 */

import type { TFunction } from 'i18next'
import type {
  DescriptorPreview,
  LeftOutRows,
  OutputNode,
  OutputSummary,
  Preview,
  Proposal,
} from '@/services/api/structuredIngest'
import { descriptorIdentity, type Identity, subjectIdentity } from './identity'

export interface WindowOutput {
  enabled: boolean
  count: number | null
  textColumns: string[]
}

/** One entity of the table as the review shows it. */
export interface EntityReview {
  node: OutputNode
  identity: Identity
  /** The preview's counts for an entity read from rows; null for the subject or before a preview. */
  stats: DescriptorPreview | null
}

export interface ReviewState {
  entities: EntityReview[]
  windows: WindowOutput
  /** Schema additions still waiting for approval. */
  schemaApprovals: string[]
  validationErrors: string[]
  /** Invalid mapped values leave an enabled entity without a usable row. */
  blockedRows: LeftOutRows | null
  /** No entity mapping is on. */
  noEntities: boolean
  /** No entity mapping, searchable row text or row extraction is selected. */
  noOutput: boolean
  /** Only searchable row text would be written. */
  searchOnly: boolean
  /** Each distinct reason adding is blocked, including rows rejected for invalid values. */
  blockers: number
}

export function sumValues(values: Record<string, number>): number {
  return Object.values(values).reduce((a, b) => a + b, 0)
}

export function hasSearchableOutput(windows: WindowOutput): boolean {
  return windows.enabled && windows.count !== 0
}

/** "55 entities", or the bare word while the count is not known yet. */
export function countPhrase(
  t: TFunction,
  kind: 'entities' | 'relationships' | 'snippets',
  count: number | null
): string {
  return count === null
    ? t(`tableMapping.count.${kind}Unknown`)
    : t(`tableMapping.count.${kind}`, { count })
}

function entityReviews(recipe: Proposal, preview: Preview, summary: OutputSummary): EntityReview[] {
  return summary.nodes.flatMap(node => {
    if (node.source === 'subject') {
      const subject = recipe.subject_entities?.find(s => s.name === node.name)
      return subject ? [{ node, identity: subjectIdentity(subject), stats: null }] : []
    }
    const descriptor = recipe.entity_descriptors?.find(d => d.name === node.name)
    if (!descriptor) return []
    const stats = preview.descriptors.find(p => p.name === node.name) ?? null
    return [{ node, identity: descriptorIdentity(descriptor, stats), stats }]
  })
}

/** A stale or failed preview cannot approve a newly invalid identity. */
function identityErrors(entities: EntityReview[], schemaApprovals: string[]): string[] {
  return entities.flatMap(({ node, identity }) => {
    if (!node.enabled) return []
    const prefix = `${node.name}:`
    if (identity.kind === 'invalid') {
      return [identity.error || `${prefix} ${identity.property} is not unique in the case schema.`]
    }
    if (identity.kind === 'missing') {
      return [
        identity.property || identity.reason === 'constant'
          ? `${prefix} an identity column or value is required.`
          : `${prefix} choose an identity property.`,
      ]
    }
    if (identity.kind === 'pending') {
      if (identity.reason === 'approval' && schemaApprovals.length > 0) return []
      return [`${prefix} identity awaits ${identity.reason} against the case schema.`]
    }
    return []
  })
}

export function reviewState(
  recipe: Proposal,
  preview: Preview,
  summary: OutputSummary,
  schemaApprovals: string[]
): ReviewState {
  const windows: WindowOutput = {
    enabled: !!recipe.windows?.enabled,
    count: preview.windows?.count ?? null,
    textColumns: preview.windows?.text_columns ?? [],
  }
  const entities = entityReviews(recipe, preview, summary)
  const leftOut = preview.left_out
  const rowError = leftOut?.blocking_error
  const blockedRows =
    rowError && leftOut
      ? { rows: leftOut.rows, cellCount: leftOut.cell_count, cells: leftOut.cells }
      : null
  const validationErrors = Array.from(
    new Set([...(preview.validation_errors ?? []), ...identityErrors(entities, schemaApprovals)])
  ).filter(error => error !== rowError)
  const noEntities = summary.nodes.every(n => !n.enabled)
  const searchable = hasSearchableOutput(windows)
  const rowExtraction = !!recipe.row_text_columns?.length && preview.extraction_cells !== 0
  const noOutput = noEntities && !searchable && !rowExtraction
  return {
    entities,
    windows,
    schemaApprovals,
    validationErrors,
    blockedRows,
    noEntities,
    noOutput,
    searchOnly: noEntities && searchable && !rowExtraction,
    blockers:
      (schemaApprovals.length > 0 ? 1 : 0) +
      validationErrors.length +
      (blockedRows ? 1 : 0) +
      (noOutput ? 1 : 0),
  }
}
